import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search, RefreshCw, ChevronLeft, ChevronRight, ExternalLink, Check, X,
  Filter, ArrowDownAZ, ArrowUpAZ, Building2, AlertTriangle, ShieldCheck, Lock, FileText, ClipboardList,
} from 'lucide-react';
import { leadsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// iter314 — Docs & Master Form split into two independent columns and
// STATUS_COLS list reflects the new backend shape (`docs`, `master_form`).
const STATUS_COLS = ['account_info', 'pef_commercials', 'drive_link', 'docs', 'master_form', 'ota_info', 'bank_details'];
const allSectionsComplete = (r) => STATUS_COLS.every(k => r[k]?.status === 'Completed');

const COLS = [
  { key: 'hotel_name', label: 'Hotel Name', type: 'text' },
  { key: 'verification', label: 'Verification', type: 'verify' },
  { key: 'account_info', label: 'Account Info', type: 'status' },
  { key: 'pef_commercials', label: 'PEF & Commercials', type: 'status' },
  { key: 'drive_link', label: 'Drive Link', type: 'status' },
  { key: 'docs', label: 'Docs', type: 'status' },
  { key: 'master_form', label: 'Master Form', type: 'status' },
  { key: 'ota_info', label: 'OTA Info', type: 'status' },
  { key: 'bank_details', label: 'Bank Details', type: 'status' },
];

const CONDITIONS = ['Equals', 'Does Not Equal', 'Contains', 'Does Not Contain', 'Is Empty', 'Is Not Empty'];

const cellValue = (row, col) => {
  if (col.type === 'text') return row.hotel_name || '';
  if (col.type === 'verify') return row.verification?.status === 'Verified' ? 'Verified' : 'Not Verified';
  return (row[col.key]?.status) || 'Pending';
};

const StatusPill = ({ status }) => {
  const ok = status === 'Completed';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
      {ok ? <Check size={12} /> : <X size={12} />}{status}
    </span>
  );
};

/* Floating, portal-rendered hover tooltip (never clipped by table).
 * The popup stays open while the cursor is over EITHER the badge OR the
 * popup itself — allowing users to move into the popup and scroll long
 * checklists. A small 120 ms close-delay bridges the gap between the two
 * elements so pointer transitions don't cause flicker.
 */
const HoverStatus = ({ col, testid }) => {
  const fields = col?.fields || [];
  const [coords, setCoords] = useState(null);
  // iter314 — Clicking a Pending badge toggles a "pending-only" view of
  // the checklist so users don't wade through completed items. Click
  // again to see everything, hover to preview.
  const [pendingOnly, setPendingOnly] = useState(false);
  const badgeRef = useRef(null);
  const closeTimerRef = useRef(null);
  const isPending = (col?.status || 'Pending') !== 'Completed';
  const visibleFields = pendingOnly ? fields.filter(f => !f.ok) : fields;

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    // Grace period so the pointer can travel from the badge to the popup
    // (and vice-versa) without the popup blinking out.
    closeTimerRef.current = setTimeout(() => setCoords(null), 120);
  };

  const show = () => {
    cancelClose();
    if (!badgeRef.current || fields.length === 0) return;
    const r = badgeRef.current.getBoundingClientRect();
    const tipW = 280;
    const spaceRight = window.innerWidth - r.left;
    const left = spaceRight < tipW + 20 ? Math.max(8, r.right - tipW) : r.left;
    const belowSpace = window.innerHeight - r.bottom;
    const estH = Math.min(40 + fields.length * 22, 320);
    // Popup starts just below the badge (with a tiny overlap so there's no
    // pointer-events gap between badge and popup).
    const top = belowSpace < estH + 16 ? Math.max(8, r.top - estH - 2) : r.bottom + 2;
    setCoords({ left, top, width: tipW });
  };

  useEffect(() => () => cancelClose(), []);

  return (
    <td className="px-3 py-3" data-testid={testid}>
      <span
        ref={badgeRef}
        onMouseEnter={show}
        onMouseLeave={scheduleClose}
        onClick={() => {
          // iter314 — click the Pending indicator to hide completed
          // items; click again to restore the full checklist. No-op
          // for Completed rows since there are no pending items to
          // isolate.
          if (isPending) setPendingOnly(v => !v);
        }}
        className={`inline-block ${isPending ? 'cursor-pointer' : 'cursor-default'}`}
        data-testid={`${testid}-badge`}
      >
        <StatusPill status={col?.status || 'Pending'} />
      </span>
      {coords && visibleFields.length > 0 && createPortal(
        <div
          className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl p-3"
          style={{ left: coords.left, top: coords.top, width: coords.width, maxHeight: 320, overflowY: 'auto' }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          data-testid={`${testid}-tooltip`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
              {pendingOnly ? 'Pending Items' : 'Checklist'}
            </p>
            {isPending && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPendingOnly(v => !v); }}
                className="text-[10px] font-medium text-teal-600 hover:text-teal-800 underline"
                data-testid={`${testid}-toggle-pending`}
              >{pendingOnly ? 'Show all' : 'Show pending only'}</button>
            )}
          </div>
          <div className="space-y-1">
            {visibleFields.map((f, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-xs">
                <span className="text-gray-700">{f.label}</span>
                {f.ok
                  ? <span className="flex items-center gap-0.5 text-emerald-600 font-medium shrink-0"><Check size={11} /> Completed</span>
                  : <span className="flex items-center gap-0.5 text-amber-600 font-medium shrink-0"><X size={11} /> Pending</span>}
              </div>
            ))}
          </div>
        </div>, document.body)}
    </td>
  );
};

/* Final-QC Verification popover (iter222) — sticky-on-hover, portal-rendered.
 * Wraps any child (the X/5 badge or the Verified pill) and shows a full
 * 5-step breakdown of the Admin verification workflow while the pointer
 * is over either the anchor or the popover itself. UI-ONLY — no logic
 * change; the flags are read from `r.verification.steps` computed by
 * `_final_qc_row` on the backend. */
const VerificationStepsHover = ({ verification, children, anchorTestId }) => {
  const steps = verification?.steps || {};
  const done = verification?.steps_completed ?? 0;
  const total = verification?.steps_total ?? 5;
  const [coords, setCoords] = useState(null);
  const anchorRef = useRef(null);
  const closeTimerRef = useRef(null);

  const cancelClose = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setCoords(null), 120);
  };
  const show = () => {
    cancelClose();
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const tipW = 260;
    const spaceRight = window.innerWidth - r.left;
    const left = spaceRight < tipW + 20 ? Math.max(8, r.right - tipW) : r.left;
    // 5 rows + header + footer ≈ 200px; place above if there's no room below.
    const estH = 220;
    const belowSpace = window.innerHeight - r.bottom;
    const top = belowSpace < estH + 16 ? Math.max(8, r.top - estH - 2) : r.bottom + 2;
    setCoords({ left, top, width: tipW });
  };
  useEffect(() => () => cancelClose(), []);

  // Same labels & order the spec requires.
  const rows = [
    { key: 'commercials', label: 'Commercials' },
    { key: 'drive_link', label: 'Drive Link' },
    { key: 'docs', label: 'Documents' },
    { key: 'ota', label: 'OTA Links' },
    { key: 'bank', label: 'Bank Details' },
  ];

  return (
    <>
      <span
        ref={anchorRef}
        onMouseEnter={show}
        onMouseLeave={scheduleClose}
        className="inline-block"
        data-testid={anchorTestId}
      >
        {children}
      </span>
      {coords && createPortal(
        <div
          className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl p-3"
          style={{ left: coords.left, top: coords.top, width: coords.width, maxHeight: 320, overflowY: 'auto' }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          data-testid={anchorTestId ? `${anchorTestId}-tooltip` : 'fqc-verify-steps-tooltip'}
        >
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Verification</p>
            <span className={`text-xs font-bold ${done >= total ? 'text-emerald-700' : done > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
              {done}/{total}
            </span>
          </div>
          <div className="space-y-1.5">
            {rows.map(({ key, label }) => {
              const ok = !!steps[key];
              return (
                <div key={key} className="flex items-center justify-between gap-2 text-xs" data-testid={`fqc-verify-step-${key}`}>
                  <span className="text-gray-700">{label}</span>
                  {ok
                    ? <span className="flex items-center gap-0.5 text-emerald-600 font-medium shrink-0"><Check size={11} /> Completed</span>
                    : <span className="flex items-center gap-0.5 text-amber-600 font-medium shrink-0"><X size={11} /> Pending</span>}
                </div>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

/* Google-Sheets-style column filter menu (portal-rendered). */
const ColumnFilterMenu = ({ col, anchorRect, state, distinctValues, onApply, onClear, onClose }) => {
  const menuRef = useRef(null);
  const [sort, setSort] = useState(state?.sort || null);
  const [condOp, setCondOp] = useState(state?.condOp || '');
  const [condVal, setCondVal] = useState(state?.condVal || '');
  const [selected, setSelected] = useState(() => state?.selected ? new Set(state.selected) : new Set(distinctValues));

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const toggleVal = (v) => {
    const next = new Set(selected);
    next.has(v) ? next.delete(v) : next.add(v);
    setSelected(next);
  };

  const left = Math.min(anchorRect.left, window.innerWidth - 280);
  const top = Math.min(anchorRect.bottom + 4, window.innerHeight - 360);

  return createPortal(
    <div ref={menuRef} className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-xl shadow-2xl p-3 text-sm" style={{ left, top }} data-testid={`col-filter-menu-${col.key}`}>
      <div className="space-y-1 pb-2 border-b border-gray-100">
        <button onClick={() => setSort('asc')} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 ${sort === 'asc' ? 'bg-teal-50 text-teal-700' : 'text-gray-700'}`} data-testid={`col-sort-asc-${col.key}`}><ArrowDownAZ size={14} /> Sort A → Z</button>
        <button onClick={() => setSort('desc')} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 ${sort === 'desc' ? 'bg-teal-50 text-teal-700' : 'text-gray-700'}`} data-testid={`col-sort-desc-${col.key}`}><ArrowUpAZ size={14} /> Sort Z → A</button>
      </div>

      <div className="py-2 border-b border-gray-100 space-y-1.5">
        <p className="text-[11px] font-bold text-gray-400 uppercase">Condition</p>
        <select value={condOp} onChange={(e) => setCondOp(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-teal-500" data-testid={`col-cond-op-${col.key}`}>
          <option value="">None</option>
          {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {condOp && !['Is Empty', 'Is Not Empty'].includes(condOp) && (
          <input value={condVal} onChange={(e) => setCondVal(e.target.value)} placeholder="Value..." className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-teal-500" data-testid={`col-cond-val-${col.key}`} />
        )}
      </div>

      <div className="py-2">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] font-bold text-gray-400 uppercase">Filter by value</p>
          <div className="flex gap-2 text-[11px]">
            <button onClick={() => setSelected(new Set(distinctValues))} className="text-teal-600 hover:underline" data-testid={`col-select-all-${col.key}`}>All</button>
            <button onClick={() => setSelected(new Set())} className="text-gray-500 hover:underline" data-testid={`col-deselect-all-${col.key}`}>None</button>
          </div>
        </div>
        <div className="max-h-40 overflow-y-auto space-y-0.5">
          {distinctValues.map(v => (
            <label key={v} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-50 cursor-pointer text-xs">
              <input type="checkbox" checked={selected.has(v)} onChange={() => toggleVal(v)} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" data-testid={`col-val-${col.key}-${v}`} />
              <span className="truncate">{v === '' ? '(empty)' : v}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button onClick={() => { onApply(col.key, { sort, condOp, condVal, selected: selected.size === distinctValues.length ? null : Array.from(selected) }); }} className="flex-1 bg-teal-600 text-white rounded-lg py-1.5 text-xs font-medium hover:bg-teal-700" data-testid={`col-apply-${col.key}`}>Apply</button>
        <button onClick={() => onClear(col.key)} className="flex-1 bg-gray-100 text-gray-600 rounded-lg py-1.5 text-xs font-medium hover:bg-gray-200" data-testid={`col-clear-${col.key}`}>Clear</button>
      </div>
    </div>, document.body);
};

export default function FinalQC() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;
  const [cardFilter, setCardFilter] = useState('all'); // all | pending | pef
  // iter314 — new header filters. All default to "no filter".
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | Completed | Pending
  const [otaFilter, setOtaFilter] = useState('all'); // all | <OTA name>
  const [filters, setFilters] = useState({}); // colKey -> {sort, condOp, condVal, selected}
  const [openFilter, setOpenFilter] = useState(null); // {key, rect}

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await leadsAPI.finalQC({ page: 1, limit: 100000 });
      setAllRows(res.data.data || []);
    } catch (e) {
      console.error('Final QC fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  // iter224 — refresh counts when the user returns to this tab after
  // performing a verification on the Accounts page. Prevents the
  // "card shows 0 while a real pending account exists" perception when the
  // Final QC page mounts before the state changed.
  useEffect(() => {
    const onFocus = () => { fetchRows(); };
    const onVis = () => { if (document.visibilityState === 'visible') fetchRows(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchRows]);

  const isPendingHotel = useCallback((r) => COLS.some(c => c.type === 'status' && r[c.key]?.status === 'Pending'), []);

  // PEF Upload pending = the 'PEF Upload' field inside PEF & Commercials is not ok.
  const isPefPending = useCallback((r) => {
    const f = (r.pef_commercials?.fields || []).find(x => x.label === 'PEF Upload');
    return f ? !f.ok : true;
  }, []);

  // Master Form pending = the dedicated `master_form` section is not Completed.
  // iter314 — reads from the new split section rather than the legacy
  // `docs_master_form.fields` list.
  const isMasterFormPending = useCallback((r) => {
    return (r.master_form?.status || 'Pending') !== 'Completed';
  }, []);

  // iter223 — "PEF & Commercials Verify" card predicate.
  // TRUE ↔ PEF is uploaded AND admin has NOT clicked Verify Commercials.
  // Reads the additive `verification.commercials_verify_pending` boolean
  // returned by `_final_qc_row` (single source of truth).
  const isCommercialsVerifyPending = useCallback((r) => {
    return !!r.verification?.commercials_verify_pending;
  }, []);

  const distinctByCol = useMemo(() => {
    const out = {};
    COLS.forEach(col => {
      if (col.type === 'status') { out[col.key] = ['Completed', 'Pending']; return; }
      if (col.type === 'verify') { out[col.key] = ['Verified', 'Not Verified']; return; }
      const set = new Set(allRows.map(r => cellValue(r, col)));
      out[col.key] = Array.from(set).sort((a, b) => a.localeCompare(b));
    });
    return out;
  }, [allRows]);

  const handleVerify = async (id) => {
    try {
      await leadsAPI.verify(id);
      setAllRows(prev => prev.map(r => r.id === id ? { ...r, verification: { ...(r.verification || {}), status: 'Verified' } } : r));
    } catch (e) { console.error('verify failed', e); }
  };

  const handleUnverify = async (id) => {
    try {
      await leadsAPI.unverify(id);
      setAllRows(prev => prev.map(r => r.id === id ? { ...r, verification: { ...(r.verification || {}), status: 'Not Verified' } } : r));
    } catch (e) { console.error('unverify failed', e); }
  };

  const applyCondition = (val, op, target) => {
    const v = (val || '').toLowerCase();
    const t = (target || '').toLowerCase();
    switch (op) {
      case 'Equals': return v === t;
      case 'Does Not Equal': return v !== t;
      case 'Contains': return v.includes(t);
      case 'Does Not Contain': return !v.includes(t);
      case 'Is Empty': return v === '';
      case 'Is Not Empty': return v !== '';
      default: return true;
    }
  };

  // iter315 — `scopedRows` is the "header-filtered" universe used to
  // drive the summary card counts. It applies Search + Date Range +
  // Status + OTA filters (the ones sitting at the top of the page)
  // but NOT the `cardFilter` itself, so clicking one card doesn't
  // zero out the others. Column-level filters and column sorts are
  // also excluded so the cards remain a stable page-header summary
  // rather than following micro-adjustments inside the table.
  const scopedRows = useMemo(() => {
    let rows = allRows;
    const s = searchTerm.trim().toLowerCase();
    if (s) rows = rows.filter(r => (r.hotel_name || '').toLowerCase().includes(s));
    if (dateFrom || dateTo) {
      const fromKey = dateFrom || null;
      const toKey = dateTo || null;
      rows = rows.filter(r => {
        const raw = r.activity_date;
        if (!raw) return false;
        const day = String(raw).slice(0, 10);
        if (fromKey && day < fromKey) return false;
        if (toKey && day > toKey) return false;
        return true;
      });
    }
    if (statusFilter === 'Completed') rows = rows.filter(r => allSectionsComplete(r));
    else if (statusFilter === 'Pending') rows = rows.filter(r => !allSectionsComplete(r));
    if (otaFilter !== 'all') {
      rows = rows.filter(r => {
        const f = (r.ota_info?.fields || []).find(x => x.label === otaFilter);
        return f && !f.ok;
      });
    }
    return rows;
  }, [allRows, searchTerm, dateFrom, dateTo, statusFilter, otaFilter]);

  const filteredRows = useMemo(() => {
    let rows = scopedRows;
    if (cardFilter === 'pending') rows = rows.filter(isPendingHotel);
    if (cardFilter === 'pef') rows = rows.filter(isPefPending);
    if (cardFilter === 'masterform') rows = rows.filter(isMasterFormPending);
    if (cardFilter === 'commercials_verify') rows = rows.filter(isCommercialsVerifyPending);
    rows = rows.filter(r => {
      for (const col of COLS) {
        const f = filters[col.key];
        if (!f) continue;
        const val = cellValue(r, col);
        if (f.selected && !f.selected.includes(val)) return false;
        if (f.condOp && !applyCondition(val, f.condOp, f.condVal)) return false;
      }
      return true;
    });
    return rows;
  }, [scopedRows, cardFilter, filters, isPendingHotel, isPefPending, isMasterFormPending, isCommercialsVerifyPending]);

  const sortedRows = useMemo(() => {
    const sortCol = COLS.find(c => filters[c.key]?.sort);
    if (!sortCol) return filteredRows;
    const dir = filters[sortCol.key].sort === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => cellValue(a, sortCol).localeCompare(cellValue(b, sortCol)) * dir);
  }, [filteredRows, filters]);

  const totalPages = Math.max(Math.ceil(sortedRows.length / limit), 1);
  const pageRows = useMemo(() => sortedRows.slice((page - 1) * limit, page * limit), [sortedRows, page]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const pendingCounts = useMemo(() => {
    const out = {};
    COLS.forEach(col => {
      if (col.type !== 'status') return;
      out[col.key] = sortedRows.filter(r => r[col.key]?.status === 'Pending').length;
    });
    return out;
  }, [sortedRows]);

  // iter315 — Summary cards now count within `scopedRows` so Date /
  // Status / OTA filters + Search flow through to Total, Pending,
  // PEF Upload, Master Form and PEF & Commercials Verify.
  const totalHotels = scopedRows.length;
  const pendingHotels = useMemo(() => scopedRows.filter(isPendingHotel).length, [scopedRows, isPendingHotel]);
  const pefPendingCount = useMemo(() => scopedRows.filter(isPefPending).length, [scopedRows, isPefPending]);
  const masterFormPendingCount = useMemo(() => scopedRows.filter(isMasterFormPending).length, [scopedRows, isMasterFormPending]);
  const commercialsVerifyPendingCount = useMemo(
    () => scopedRows.filter(isCommercialsVerifyPending).length,
    [scopedRows, isCommercialsVerifyPending]
  );

  // iter314 — Distinct OTA names across ALL rows + pending count per OTA.
  // Used to populate the OTA filter dropdown and show "Agoda (12)"-style
  // counts so operators can see the outstanding backlog at a glance.
  const otaFilterOptions = useMemo(() => {
    const counts = new Map();
    for (const r of allRows) {
      for (const f of (r.ota_info?.fields || [])) {
        if (!f?.label) continue;
        const cur = counts.get(f.label) || { total: 0, pending: 0 };
        cur.total += 1;
        if (!f.ok) cur.pending += 1;
        counts.set(f.label, cur);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, c]) => ({ name, pending: c.pending, total: c.total }));
  }, [allRows]);

  const headerLabel = (col) => (col.type === 'status' ? `${col.label} (${pendingCounts[col.key] ?? 0})` : col.label);
  const isColFiltered = (key) => { const f = filters[key]; return f && (f.sort || f.condOp || f.selected); };

  return (
    <div className="space-y-4" data-testid="final-qc-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-stretch gap-3">
        <h1 className="text-2xl font-bold text-gray-800 mt-1">Final QC</h1>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <input type="text" placeholder="Search by Hotel Name..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }} data-testid="final-qc-search" />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            </div>
            <button onClick={fetchRows} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-gray-100 rounded-lg" title="Refresh" data-testid="final-qc-refresh"><RefreshCw size={18} /></button>
          </div>
          {/* iter314 — Date-range filter under the search bar. `activity_date`
              on each row falls back through updated_at → closed_won_at →
              verified_at → account_locked_at → created_at. */}
          <div className="flex items-center gap-2 text-xs text-gray-600" data-testid="final-qc-date-range">
            <span className="uppercase tracking-wide font-semibold text-gray-500">Date</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-teal-500 outline-none"
              data-testid="final-qc-date-from"
              max={dateTo || undefined}
              placeholder="From"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-teal-500 outline-none"
              data-testid="final-qc-date-to"
              min={dateFrom || undefined}
              placeholder="To"
            />
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                className="text-teal-600 hover:text-teal-800 underline"
                data-testid="final-qc-date-clear"
              >Clear</button>
            )}
          </div>
          {/* iter314 — Status dropdown (Completed/Pending). Default 'All'. */}
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="uppercase tracking-wide font-semibold text-gray-500">Status</span>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-teal-500 outline-none bg-white"
              data-testid="final-qc-status-filter"
            >
              <option value="all">All statuses</option>
              <option value="Completed">Completed (everything done)</option>
              <option value="Pending">Pending (any incomplete item)</option>
            </select>
            {/* iter314 — OTA filter dropdown. Counts pending per OTA. */}
            <span className="uppercase tracking-wide font-semibold text-gray-500 ml-2">OTA</span>
            <select
              value={otaFilter}
              onChange={(e) => { setOtaFilter(e.target.value); setPage(1); }}
              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-teal-500 outline-none bg-white"
              data-testid="final-qc-ota-filter"
            >
              <option value="all">All OTAs</option>
              {otaFilterOptions.map(o => (
                <option key={o.name} value={o.name}>{o.name} ({o.pending} pending)</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-5xl">
        <button onClick={() => { setCardFilter('all'); setPage(1); }} className={`text-left p-4 rounded-xl border transition-all ${cardFilter === 'all' ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-300' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid="card-total-hotels">
          <div className="flex items-center gap-2 text-gray-500"><Building2 size={16} /><span className="text-xs font-medium uppercase tracking-wide">Total Hotels</span></div>
          <p className="text-2xl font-bold text-gray-800 mt-1" data-testid="card-total-hotels-count">{totalHotels}</p>
        </button>
        <button onClick={() => { setCardFilter('pending'); setPage(1); }} className={`text-left p-4 rounded-xl border transition-all ${cardFilter === 'pending' ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-300' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid="card-pending">
          <div className="flex items-center gap-2 text-amber-600"><AlertTriangle size={16} /><span className="text-xs font-medium uppercase tracking-wide">Pending</span></div>
          <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="card-pending-count">{pendingHotels}</p>
        </button>
        <button onClick={() => { setCardFilter('pef'); setPage(1); }} className={`text-left p-4 rounded-xl border transition-all ${cardFilter === 'pef' ? 'border-rose-500 bg-rose-50 ring-1 ring-rose-300' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid="card-pef-upload">
          <div className="flex items-center gap-2 text-rose-600"><FileText size={16} /><span className="text-xs font-medium uppercase tracking-wide">PEF Upload</span></div>
          <p className="text-2xl font-bold text-rose-700 mt-1" data-testid="card-pef-upload-count">{pefPendingCount}</p>
        </button>
        <button onClick={() => { setCardFilter('masterform'); setPage(1); }} className={`text-left p-4 rounded-xl border transition-all ${cardFilter === 'masterform' ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-testid="card-master-form">
          <div className="flex items-center gap-2 text-indigo-600"><ClipboardList size={16} /><span className="text-xs font-medium uppercase tracking-wide">Master Form</span></div>
          <p className="text-2xl font-bold text-indigo-700 mt-1" data-testid="card-master-form-count">{masterFormPendingCount}</p>
        </button>
        {/* iter223 — PEF & Commercials Verify pending. Reuses the existing
            Admin `Verify Commercials` state; disappears from this list the
            moment Admin clicks Verify in the Accounts tab. */}
        <button
          onClick={() => { setCardFilter('commercials_verify'); setPage(1); }}
          className={`text-left p-4 rounded-xl border transition-all ${cardFilter === 'commercials_verify' ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-300' : 'border-gray-200 bg-white hover:border-gray-300'}`}
          title="Accounts with PEF uploaded where Admin has not yet clicked 'Verify Commercials'"
          data-testid="card-commercials-verify"
        >
          <div className="flex items-center gap-2 text-purple-600">
            <ShieldCheck size={16} />
            <span className="text-xs font-medium uppercase tracking-wide">PEF &amp; Commercials Verify</span>
          </div>
          <p className="text-2xl font-bold text-purple-700 mt-1" data-testid="card-commercials-verify-count">{commercialsVerifyPendingCount}</p>
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[1100px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {COLS.map(col => (
                <th key={col.key} className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <span className="inline-flex items-center gap-1.5">
                    {headerLabel(col)}
                    <button
                      onClick={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setOpenFilter(openFilter?.key === col.key ? null : { key: col.key, rect }); }}
                      className={`p-0.5 rounded hover:bg-gray-200 ${isColFiltered(col.key) ? 'text-teal-600' : 'text-gray-400'}`}
                      title="Filter / Sort"
                      data-testid={`col-filter-btn-${col.key}`}
                    ><Filter size={13} /></button>
                  </span>
                </th>
              ))}
              <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">Loading...</td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">No accounts found</td></tr>
            ) : pageRows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50" data-testid={`final-qc-row-${r.id}`}>
                <td className="px-3 py-3 text-sm font-medium text-gray-900">{r.hotel_name || '-'}</td>
                <td className="px-3 py-3" data-testid={`fqc-verification-${r.id}`}>
                  {r.verification?.status === 'Verified' ? (
                    <div className="flex items-center gap-2">
                      <VerificationStepsHover verification={r.verification} anchorTestId={`fqc-verified-badge-anchor-${r.id}`}>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`fqc-verified-badge-${r.id}`}>
                          <Lock size={12} /> Verified
                        </span>
                      </VerificationStepsHover>
                      {isAdmin && (
                        <button onClick={() => handleUnverify(r.id)} className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-50 text-rose-600 border border-rose-200 text-xs font-medium rounded-lg hover:bg-rose-100" data-testid={`fqc-unverify-btn-${r.id}`}>
                          <X size={12} /> Unverify
                        </button>
                      )}
                    </div>
                  ) : (() => {
                    // Per Final-QC spec — replace the "Not Verified" badge with
                    // a 5-step Admin-verification progress count (Completed / Total).
                    // Once all 5 steps are ticked, admin can click the pill to
                    // trigger the existing full-account verify/lock workflow.
                    const done = r.verification?.steps_completed ?? 0;
                    const total = r.verification?.steps_total ?? 5;
                    const ready = done >= total;
                    return (
                      <VerificationStepsHover verification={r.verification} anchorTestId={`fqc-step-progress-anchor-${r.id}`}>
                        <button
                          onClick={() => ready && handleVerify(r.id)}
                          disabled={!ready}
                          title={ready
                            ? 'All 5 verification steps completed — click to verify & lock the account'
                            : `${done}/${total} Final-QC verification steps completed. Hover to see the breakdown.`}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg ${
                            ready
                              ? 'bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer'
                              : done === 0
                                ? 'bg-gray-100 text-gray-500 border border-gray-200 cursor-default'
                                : 'bg-amber-50 text-amber-700 border border-amber-200 cursor-default'
                          }`}
                          data-testid={`fqc-step-progress-${r.id}`}
                        >
                          <ShieldCheck size={12} /> {done}/{total}
                        </button>
                      </VerificationStepsHover>
                    );
                  })()}
                </td>
                <HoverStatus col={r.account_info} testid={`fqc-account-info-${r.id}`} />
                <HoverStatus col={r.pef_commercials} testid={`fqc-pef-${r.id}`} />
                <HoverStatus col={r.drive_link} testid={`fqc-drive-${r.id}`} />
                <HoverStatus col={r.docs} testid={`fqc-docs-${r.id}`} />
                <HoverStatus col={r.master_form} testid={`fqc-master-form-${r.id}`} />
                <HoverStatus col={r.ota_info} testid={`fqc-ota-${r.id}`} />
                <HoverStatus col={r.bank_details} testid={`fqc-bank-${r.id}`} />
                <td className="px-3 py-3">
                  <button onClick={() => window.open(`/leads/${r.id}`, '_blank', 'noopener,noreferrer')} className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700" data-testid={`fqc-open-${r.id}`}>Open <ExternalLink size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-400">{sortedRows.length} of {totalHotels} accounts</span>
        {totalPages > 1 && (
          <div className="flex items-center border rounded-md overflow-hidden">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 border-r hover:bg-gray-50 disabled:opacity-50"><ChevronLeft size={16} /></button>
            <span className="px-4 py-1 text-sm font-medium text-gray-700 bg-gray-50">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 border-l hover:bg-gray-50 disabled:opacity-50"><ChevronRight size={16} /></button>
          </div>
        )}
      </div>

      {openFilter && (
        <ColumnFilterMenu
          col={COLS.find(c => c.key === openFilter.key)}
          anchorRect={openFilter.rect}
          state={filters[openFilter.key]}
          distinctValues={distinctByCol[openFilter.key] || []}
          onApply={(key, st) => { setFilters(prev => ({ ...prev, [key]: st })); setOpenFilter(null); setPage(1); }}
          onClear={(key) => { setFilters(prev => { const n = { ...prev }; delete n[key]; return n; }); setOpenFilter(null); setPage(1); }}
          onClose={() => setOpenFilter(null)}
        />
      )}
    </div>
  );
}
